import { useState } from "react";
import { DateTime } from "luxon";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  LinkIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/20/solid";
import PrimaryButton from "../../Shared/PrimaryButton";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import AlertYellow from "../../Shared/AlertYellow";
import RegenerateSection from "./RegenerateSection";
import {
  useCalendarFeed,
  useCreateCalendarFeed,
} from "../../../hooks/useCalendarFeed";
import { useToast } from "../../../hooks/useToast";
import {
  getCalendarSubscribeLinks,
  detectCalendarPlatform,
} from "../../../utility/environment";

/** Masks a feed URL, keeping the host visible and hiding the token. */
const maskFeedUrl = (feedUrl) => {
  if (!feedUrl) return "";
  try {
    const url = new URL(feedUrl);
    return `${url.origin}/…/••••••••.ics`;
  } catch {
    return feedUrl.replace(/\/[^/]+\.ics$/, "/••••••••.ics");
  }
};

const formatRotatedAt = (rotatedAt) =>
  DateTime.fromISO(rotatedAt).toLocaleString(DateTime.DATETIME_MED);

const PLATFORM_LABELS = {
  apple: "Subscribe in Apple Calendar",
  google: "Subscribe in Google Calendar",
};

/**
 * Card-body padding wrapper matching TabFormWrapper's styling, but a plain
 * `<div>` rather than a `<form>`. Neither of this file's two top-level views
 * submits anything directly anymore — the only actual `<form>` lives inside
 * RegenerateSection (rendered as a descendant) — so wrapping them in a second,
 * outer `<form>` would produce invalid nested-form HTML.
 */
const CardBody = ({ children }) => (
  <div className="px-4 py-6 sm:p-8">{children}</div>
);

/** Intro copy + privacy warning shown above both the full and hidden views. */
const IntroSection = () => (
  <>
    <p className="text-sm text-ink-600 mb-4">
      Subscribe to see your Anyspot schedule in Google, Apple, or Outlook
      Calendar — it updates automatically.
    </p>
    <div className="mb-6">
      <AlertYellow message="Anyone with this link can see your full schedule, including client details. Keep it private." />
    </div>
  </>
);

/**
 * Full view: rendered once a usable (non-null) feedUrl is available — either
 * from the very first GET, or after a successful regenerate. Shows the
 * platform-aware subscribe buttons, the masked/copyable raw URL, and the
 * regenerate control.
 */
const CalendarFeedFormContent = ({ feedUrl, rotatedAt }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const links = getCalendarSubscribeLinks(feedUrl);
  const primaryPlatform = detectCalendarPlatform();
  const primaryLink = primaryPlatform === "apple" ? links.apple : links.google;
  const primaryLabel =
    PLATFORM_LABELS[primaryPlatform] || PLATFORM_LABELS.google;

  const copyLink = async () => {
    if (!links?.raw) return;
    try {
      await navigator.clipboard.writeText(links.raw);
      setCopied(true);
      toast.success("Link Copied", "Calendar feed link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy Failed", "Could not copy link to clipboard");
    }
  };

  return (
    <CardBody>
      <IntroSection />

      {/* Primary subscribe button (platform-aware hero action) */}
      <div className="mb-4">
        <a
          href={primaryLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block"
        >
          <PrimaryButton colour="blue" type="button">
            {primaryLabel}
          </PrimaryButton>
        </a>
      </div>

      {/* Expectation-setting copy */}
      <p className="mb-6 text-sm text-ink-600">
        Changes can take up to a day to appear — that&rsquo;s your calendar
        app&rsquo;s refresh schedule, not Anyspot. Cancelled appointments drop
        off on the next refresh.
      </p>

      {/* Use a different calendar */}
      <Disclosure>
        <DisclosureButton className="flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700">
          <ChevronDownIcon className="h-4 w-4 transition-transform data-[open]:rotate-180" />
          Use a different calendar
        </DisclosureButton>
        <DisclosurePanel className="mt-3 space-y-4">
          <div className="flex flex-wrap gap-2">
            {primaryPlatform !== "apple" && (
              <a
                href={links.apple}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-pill bg-white px-4 py-2 text-sm font-medium text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 transition-colors"
              >
                Apple Calendar
              </a>
            )}
            {primaryPlatform !== "google" && (
              <a
                href={links.google}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-pill bg-white px-4 py-2 text-sm font-medium text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 transition-colors"
              >
                Google Calendar
              </a>
            )}
            <a
              href={links.outlook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-pill bg-white px-4 py-2 text-sm font-medium text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 transition-colors"
            >
              Outlook Calendar
            </a>
          </div>
        </DisclosurePanel>
      </Disclosure>

      {/* Raw feed URL (fallback, masked by default) */}
      <div className="mt-6 border-t border-ink-100 pt-6">
        <Disclosure>
          <DisclosureButton className="flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700">
            <ChevronDownIcon className="h-4 w-4 transition-transform data-[open]:rotate-180" />
            Add the link manually
          </DisclosureButton>
          <DisclosurePanel className="mt-3">
            <p className="text-sm text-ink-600 mb-3">
              Prefer to add it manually? Copy this link, then use your
              calendar app&rsquo;s <strong>Subscribe</strong> /{" "}
              <strong>From URL</strong> option — not <strong>Import</strong>.
            </p>

            <div className="flex items-center gap-x-2 bg-white rounded-md border border-ink-100 px-3 py-2.5">
              <LinkIcon className="size-3.5 text-ink-400 flex-shrink-0" />
              <span
                className="text-xs font-mono text-ink-600 truncate flex-1 break-all"
                aria-label="Your calendar feed URL"
              >
                {revealed ? links.raw : maskFeedUrl(links.raw)}
              </span>
              <button
                type="button"
                onClick={() => setRevealed((r) => !r)}
                className="text-ink-400 hover:text-ink-700 flex-shrink-0"
                aria-label={revealed ? "Hide link" : "Show link"}
              >
                {revealed ? (
                  <EyeSlashIcon className="size-4" />
                ) : (
                  <EyeIcon className="size-4" />
                )}
              </button>
            </div>

            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 transition-colors"
              >
                {copied ? (
                  <>
                    <CheckIcon className="size-3.5 text-signal-green-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="size-3.5" />
                    Copy Link
                  </>
                )}
              </button>
            </div>

            {rotatedAt && (
              <p className="mt-3 text-xs text-ink-400">
                Last regenerated {formatRotatedAt(rotatedAt)}
              </p>
            )}
          </DisclosurePanel>
        </Disclosure>
      </div>

      {/* Regenerate link (destructive, password-gated) */}
      <div className="mt-8 border-t border-ink-100 pt-6">
        <RegenerateSection onSuccess={() => setRevealed(false)} />
      </div>
    </CardBody>
  );
};

/**
 * Hidden-link view: rendered when the API returns feedUrl: null — every
 * return visit after the first, since the raw token is only ever knowable
 * at creation ("create-once + rotate-to-reveal"). Explains why the link
 * isn't shown and always offers a working path forward via Regenerate,
 * instead of the previous dead-end empty state.
 */
const CalendarFeedHiddenState = ({ rotatedAt }) => (
  <CardBody>
    <IntroSection />

    <div className="rounded-card border border-ink-100 bg-cream-50 p-4">
      <p className="text-sm text-ink-700">
        For security, your link is only shown once, when it&rsquo;s created.
      </p>
      <p className="mt-2 text-sm text-ink-600">
        If it&rsquo;s still saved in your calendar app, it keeps working. Lost
        it? Regenerate below.
      </p>
      {rotatedAt && (
        <p className="mt-3 text-xs text-ink-400">
          Last generated {formatRotatedAt(rotatedAt)}
        </p>
      )}
    </div>

    <div className="mt-8 border-t border-ink-100 pt-6">
      <RegenerateSection
        heading="Regenerate link"
        description="Enter your password to create a fresh link."
      />
    </div>
  </CardBody>
);

/**
 * Not-generated view: rendered for a provider who has never created a feed
 * token (fresh account, or hasn't clicked "Generate link" yet). The token is
 * no longer created just by viewing this section — it's created only on this
 * explicit, unguarded action (nothing to protect on first create: no prior
 * token, no connected calendars that could break).
 */
const CalendarFeedNotGeneratedState = () => {
  const toast = useToast();
  const { mutate: doCreate, isPending: isCreating } = useCreateCalendarFeed();

  const handleGenerate = () => {
    doCreate(undefined, {
      onError: (err) => {
        toast.error(
          "Error",
          err.response?.data?.error?.message ||
            "Failed to generate calendar feed link"
        );
      },
    });
  };

  return (
    <CardBody>
      <IntroSection />

      <p className="text-sm text-ink-600 mb-4">
        Generate a link to subscribe from Google, Apple, or Outlook Calendar.
      </p>

      <PrimaryButton
        colour="blue"
        type="button"
        onClick={handleGenerate}
        disabled={isCreating}
      >
        {isCreating ? "Generating…" : "Generate link"}
      </PrimaryButton>
    </CardBody>
  );
};

const CalendarFeedForm = () => {
  const { data, isLoading } = useCalendarFeed();

  if (isLoading) {
    return (
      <TabFormWrapper>
        <div className="flex justify-center items-center py-8">
          <div className="text-ink-500">Loading...</div>
        </div>
      </TabFormWrapper>
    );
  }

  // Order matters: check feedUrl first so a fresh create/rotate always wins,
  // then hasToken (hidden/recovery state), else the never-generated state.
  if (data?.feedUrl) {
    return (
      <CalendarFeedFormContent
        feedUrl={data.feedUrl}
        rotatedAt={data.rotatedAt}
      />
    );
  }

  if (data?.hasToken) {
    return <CalendarFeedHiddenState rotatedAt={data?.rotatedAt} />;
  }

  return <CalendarFeedNotGeneratedState />;
};

export default CalendarFeedForm;
